import { useState, useEffect, useMemo, useRef } from 'react';
import { publicApiClient } from '@/lib/api/client';
import { cartService } from '@/features/cart/services/cartService';
import { authService } from '@/features/user/services/authService';
import { useAuth } from '@/hooks/useAuth';
import { CartStatus } from '@/features/cart/types';
import type {
  TicketBookingState,
  EventScheduleData,
  EventBookingCategoryData,
  PriceBreakdown,
  AddOnScheduleData,
  AddOnSelection
} from '@/features/booking/types/ticketBookingTypes';
import type { EventBookingCategoryWithAddOnSchedulesResponse } from '@/features/booking/types/eventBookingCategoryTypes';
import type { Business } from '@/features/booking/types/tableBookingTypes';
import type { CartResponse } from '@/features/cart/types';

export const useTicketBooking = (eventBookingCategoryId: string, initialCartTab?: 'main' | 'guest' | null, cartItemId?: string | null) => {
  const { isAuthenticated, refetch: refetchAuth } = useAuth();
  const [state, setState] = useState<TicketBookingState>({
    eventSchedule: null,
    eventBookingCategory: null,
    guestCount: 1,
    addOnIdsPerGuest: [[]], // Start with one empty guest
    selectedAddOns: {},
    loading: true,
    error: null,
    addingToCart: false,
  });

  // Cart-related state
  const [incompleteCarts, setIncompleteCarts] = useState<CartResponse[] | null>(null);
  const [selectedCartTab, setSelectedCartTab] = useState<'main' | 'guest' | null>(null);
  const [isLoadingCarts, setIsLoadingCarts] = useState(false);
  const [isUpdatingCart, setIsUpdatingCart] = useState(false);
  const isLoadingCartsRef = useRef(false);
  // Track if we're in the middle of addToCart to prevent double fetch
  const isAddingToCartRef = useRef(false);
  const isTabSwitchingRef = useRef(false);

  // Per-tab saved local state — preserves local edits when switching tabs
  const savedStatePerTab = useRef<{
    main: {
      guestCount: number;
      addOnIdsPerGuest: string[][];
      selectedAddOns: Record<string, number>;
    } | null;
    guest: {
      guestCount: number;
      addOnIdsPerGuest: string[][];
      selectedAddOns: Record<string, number>;
    } | null;
  }>({ main: null, guest: null });

  // Helper function to get current cart based on selected tab
  const getCurrentCart = (): CartResponse | null => {
    if (!incompleteCarts || incompleteCarts.length === 0 || !selectedCartTab) {
      return null;
    }

    if (selectedCartTab === 'main') {
      return incompleteCarts.find(c => !c.is_guest_cart) || null;
    } else {
      return incompleteCarts.find(c => c.is_guest_cart) || null;
    }
  };

  // Helper function to determine default tab based on carts and initial preference
  const determineDefaultTab = (carts: CartResponse[]): 'main' | 'guest' | null => {
    if (carts.length === 0) {
      return null;
    }

    const mainCart = carts.find(c => !c.is_guest_cart);
    const guestCart = carts.find(c => c.is_guest_cart);

    // If initialCartTab is provided and the corresponding cart exists, use it
    // This is especially useful when both carts exist and we want to prioritize the source cart
    if (initialCartTab) {
      if (initialCartTab === 'main' && mainCart) {
        return 'main';
      }
      if (initialCartTab === 'guest' && guestCart) {
        return 'guest';
      }
    }

    // Fall back to existing logic:
    // Case 1: Both carts exist → select 'main'
    if (mainCart && guestCart) {
      return 'main';
    }
    // Case 2: Only guest cart exists → select 'guest'
    if (guestCart) {
      return 'guest';
    }
    // Case 3: Only main cart exists → select 'main'
    if (mainCart) {
      return 'main';
    }

    return null;
  };

  // Fetch event booking category data and incomplete carts in parallel
  const fetchData = async (skipLoadingState = false) => {
    if (!eventBookingCategoryId) {
      return;
    }

    if (!skipLoadingState) {
      setState(prev => ({ ...prev, loading: true, error: null }));
    } else {
      setState(prev => ({ ...prev, error: null }));
    }

    // Prepare promises for parallel execution
    type EventBookingCategoryResult = {
      eventScheduleData: EventScheduleData;
      eventBookingCategoryData: EventBookingCategoryData;
    };
    const promises: Array<Promise<EventBookingCategoryResult | CartResponse[]>> = [];

    // Promise 1: Fetch event booking category (includes event schedule)
    const eventBookingCategoryPromise = (async (): Promise<EventBookingCategoryResult> => {
      try {
        const response = await publicApiClient.get<EventBookingCategoryWithAddOnSchedulesResponse>(
          `/api/public/v1/event-booking-category/${eventBookingCategoryId}`
        );

        // Validate response structure
        if (!response || !response.data || !response.data.event_booking_category) {
          throw new Error('Invalid response structure from API');
        }

        // Extract event schedule from the response
        const eventScheduleFromResponse = response.data.event_booking_category.event_schedule;

        // Map event schedule to EventScheduleData format
        const eventScheduleData: EventScheduleData = {
          id: eventScheduleFromResponse.id,
          start_time: eventScheduleFromResponse.start_time,
          duration: eventScheduleFromResponse.duration,
          early_entry_duration: eventScheduleFromResponse.early_entry_duration ?? null,
          cancelled: eventScheduleFromResponse.cancelled,
          event: {
            id: eventScheduleFromResponse.event.id,
            name: eventScheduleFromResponse.event.name,
            description: eventScheduleFromResponse.event.description,
            card_image_id: eventScheduleFromResponse.event.card_image_id,
            base_image_id: eventScheduleFromResponse.event.base_image_id,
            base_video_id: eventScheduleFromResponse.event.base_video_id,
            business: eventScheduleFromResponse.event.business ? {
              id: eventScheduleFromResponse.event.business.id,
              name: eventScheduleFromResponse.event.business.name,
              handle: eventScheduleFromResponse.event.business.handle,
              card_image_id: eventScheduleFromResponse.event.business.card_image_id,
              base_image_id: eventScheduleFromResponse.event.business.base_image_id,
              base_video_id: eventScheduleFromResponse.event.business.base_video_id,
              location: {
                city: eventScheduleFromResponse.event.business.location.city,
                timezone_id: eventScheduleFromResponse.event.business.location.timezone_id
              }
            } : null
          },
          timezone_id: eventScheduleFromResponse.event.business?.location?.timezone_id || 'Europe/London',
          add_on_schedules: [] // Not needed from this response
        };

        // Map the API response add-on schedules to AddOnScheduleData format
        // Handle case where add_on_schedules might be null or undefined
        const addOnSchedules: AddOnScheduleData[] = (response.data.add_on_schedules || []).map(aos => ({
          price: aos.price,
          inventory_count: aos.inventory_count,
          remaining_inventory: aos.remaining_inventory,
          add_on: {
            id: aos.add_on.id,
            name: aos.add_on.name,
            description: aos.add_on.description,
            published: aos.add_on.published,
            default_price: aos.add_on.default_price,
            default_inventory_count: aos.add_on.default_inventory_count,
            image_id: aos.add_on.image_id,
            business: aos.add_on.business as Business | null,
            allow_multiple_per_guest: (aos.add_on as { allow_multiple_per_guest?: boolean }).allow_multiple_per_guest ?? false,
            is_platform_add_on: (aos.add_on as { is_platform_add_on?: boolean }).is_platform_add_on ?? false
          },
          event_schedule: null // Not needed for ticket booking
        }));

        // Map EventBookingCategoryResponse to EventBookingCategoryData
        const ebc = response.data.event_booking_category;
        const eventBookingCategoryData: EventBookingCategoryData = {
          id: ebc.id,
          cancelled: ebc.cancelled,
          conflict_detected: ebc.conflict_detected,
          capacity: ebc.capacity,
          remaining_capacity: ebc.remaining_capacity,
          price: ebc.price,
          cancellation_fees: ebc.cancellation_fees,
          booking_category: {
            id: ebc.booking_category.id,
            name: ebc.booking_category.name,
            description: ebc.booking_category.description,
            is_table_booking: ebc.booking_category.is_table_booking,
            card_image_id: ebc.booking_category.card_image_id,
            card_video_id: ebc.booking_category.card_video_id,
            base_image_id: ebc.booking_category.base_image_id,
            perks: ebc.booking_category.perks || [],
            allowed_areas: ebc.booking_category.allowed_areas || null,
            class: ebc.booking_category.class,
            business: ebc.booking_category.business ? {
              id: ebc.booking_category.business.id,
              name: ebc.booking_category.business.name,
              handle: ebc.booking_category.business.handle,
              card_image_id: ebc.booking_category.business.card_image_id,
              base_image_id: ebc.booking_category.business.base_image_id,
              location: {
                city: ebc.booking_category.business.location.city,
                timezone_id: ebc.booking_category.business.location.timezone_id
              }
            } : null,
            media: (ebc.booking_category.media || []).map(m => ({
              media_id: m.media_id,
              media_type: m.media_type,
              rank: m.rank ?? 0
            }))
          },
          currency: ebc.currency || 'USD',
          add_on_schedules: addOnSchedules
        };

        return { eventScheduleData, eventBookingCategoryData };
      } catch (error) {
        console.error('Error fetching event booking category:', error);
        throw error;
      }
    })();
    promises.push(eventBookingCategoryPromise);

    // Promise 2: Fetch incomplete carts (only if authenticated)
    let incompleteCartsPromise: Promise<CartResponse[]> | null = null;
    if (isAuthenticated && !isLoadingCartsRef.current) {
      isLoadingCartsRef.current = true;
      setIsLoadingCarts(true);
      incompleteCartsPromise = (async (): Promise<CartResponse[]> => {
        try {
          const carts = await cartService.getIncompleteCartsForEBC(eventBookingCategoryId);

          // Handle edge case: if more than 2 carts, log error and take most recent 2
          if (carts.length > 2) {
            console.error(`Unexpected: Found ${carts.length} incomplete carts for EBC. Taking most recent 2.`);
            // Sort by created_at (assuming carts have this field, or use id as fallback)
            const sortedCarts = [...carts].sort((a, b) => {
              // Try to use expires_at as proxy for creation time (newer carts expire later)
              return new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime();
            });
            carts.splice(0, carts.length, ...sortedCarts.slice(0, 2));
          }

          return carts;
        } catch (error) {
          console.error('Error fetching incomplete carts:', error);
          // On network error, return empty array (will retry later if needed)
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
            // Return empty array, retry will happen after a delay if needed
            return [];
          }
          throw error;
        }
      })();
      promises.push(incompleteCartsPromise);
    }

    // Execute all promises in parallel
    try {
      const results = await Promise.allSettled(promises);

      // Process event booking category result
      const ebcResult = results[0];
      if (ebcResult.status === 'fulfilled') {
        const result = ebcResult.value as EventBookingCategoryResult;
        const { eventScheduleData, eventBookingCategoryData } = result;
        setState(prev => ({
          ...prev,
          eventSchedule: eventScheduleData,
          eventBookingCategory: eventBookingCategoryData,
          loading: false
        }));
      } else {
        // Event booking category failed
        setState(prev => ({
          ...prev,
          loading: false,
          error: ebcResult.reason instanceof Error ? ebcResult.reason.message : 'Failed to fetch booking category'
        }));
      }

      // Process incomplete carts result (if it was executed)
      if (incompleteCartsPromise && results.length > 1) {
        const cartsResult = results[1];
        if (cartsResult.status === 'fulfilled') {
          const carts = cartsResult.value as CartResponse[];
          setIncompleteCarts(carts);

          // Set default tab using helper function that considers initialCartTab preference
          const defaultTab = determineDefaultTab(carts);
          setSelectedCartTab(defaultTab);
        } else {
          // Cart fetch failed - handle retry for network errors
          const errorMessage = cartsResult.reason instanceof Error ? cartsResult.reason.message : 'Unknown error';
          if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
            // Retry once after a short delay
            setTimeout(async () => {
              try {
                if (eventBookingCategoryId && !isLoadingCartsRef.current) {
                  isLoadingCartsRef.current = true;
                  const retryCarts = await cartService.getIncompleteCartsForEBC(eventBookingCategoryId);
                  setIncompleteCarts(retryCarts);

                  // Set default tab using helper function that considers initialCartTab preference
                  const defaultTab = determineDefaultTab(retryCarts);
                  setSelectedCartTab(defaultTab);
                  isLoadingCartsRef.current = false;
                }
              } catch (retryError) {
                console.error('Retry failed for incomplete carts:', retryError);
                setIncompleteCarts([]);
                isLoadingCartsRef.current = false;
              }
            }, 1000);
          } else {
            setIncompleteCarts([]);
          }
        }
        setIsLoadingCarts(false);
        isLoadingCartsRef.current = false;
      }
    } catch (error) {
      // This shouldn't happen with Promise.allSettled, but handle it just in case
      console.error('Unexpected error in fetchData:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch data'
      }));
      setIsLoadingCarts(false);
      isLoadingCartsRef.current = false;
    }
  };

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    // Skip fetch if we're in the middle of adding to cart (will be handled by addToCart itself)
    if (isAddingToCartRef.current) {
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBookingCategoryId, isAuthenticated]);

  // Preselect cart items when cart exists
  useEffect(() => {
    if (!incompleteCarts || incompleteCarts.length === 0 || !state.eventBookingCategory || !selectedCartTab) {
      isTabSwitchingRef.current = false;
      return;
    }

    // Check if there's saved local state for this tab (user had local edits before switching away)
    const savedState = savedStatePerTab.current[selectedCartTab];
    if (savedState) {
      // Restore the saved local state instead of loading from cart
      setState(prev => ({
        ...prev,
        guestCount: savedState.guestCount,
        addOnIdsPerGuest: savedState.addOnIdsPerGuest,
        selectedAddOns: savedState.selectedAddOns,
      }));
      // Clear saved state — it's now in the active state
      savedStatePerTab.current[selectedCartTab] = null;
      isTabSwitchingRef.current = false;
      return;
    }

    // Get current cart based on selected tab
    let currentCart: CartResponse | null = null;
    if (selectedCartTab === 'main') {
      currentCart = incompleteCarts.find(c => !c.is_guest_cart) || null;
    } else {
      currentCart = incompleteCarts.find(c => c.is_guest_cart) || null;
    }

    // Items array is already filtered by EBC - if it has items, they're for this EBC
    // If items is empty, there's no item for this EBC in the cart
    if (!currentCart || !currentCart.items || currentCart.items.length === 0) {
      // Reset state when switching to a tab with no items
      setState(prev => ({
        ...prev,
        guestCount: 1,
        addOnIdsPerGuest: [[]],
        selectedAddOns: {}
      }));
      isTabSwitchingRef.current = false;
      return;
    }

    // The items array contains only items for this EBC (or is empty)
    // Find the specific cart item if cartItemId is provided, otherwise use the first item
    let matchingItem = currentCart.items[0];
    if (cartItemId) {
      matchingItem = currentCart.items.find(item => item.id === cartItemId) || currentCart.items[0];
    }

    if (matchingItem) {
      // Preselect guest count and add-ons
      // Convert add_ons_per_guest from SelectedAddOn[][] to string[][]
      // Expand quantities: if quantity is 3, add the add_on_id 3 times
      const addOnIdsPerGuest = matchingItem.add_ons_per_guest?.map(guestAddOns => {
        const expandedIds: string[] = [];
        guestAddOns?.forEach(addOn => {
          if (addOn) {
            const qty = addOn.quantity || 1;
            for (let i = 0; i < qty; i++) {
              expandedIds.push(addOn.add_on_id);
            }
          }
        });
        return expandedIds;
      }) || [[]];

      setState(prev => ({
        ...prev,
        guestCount: matchingItem.total_guests,
        addOnIdsPerGuest: addOnIdsPerGuest,
        selectedAddOns: matchingItem.add_ons_per_guest?.reduce((acc, guestAddOns) => {
          guestAddOns?.forEach(addOn => {
            if (addOn) {
              // Use quantity from the add-on, default to 1 if not present
              const qty = addOn.quantity || 1;
              acc[addOn.add_on_id] = (acc[addOn.add_on_id] || 0) + qty;
            }
          });
          return acc;
        }, {} as Record<string, number>) || {}
      }));
    } else {
      // Reset state if no matching item found
      setState(prev => ({
        ...prev,
        guestCount: 1,
        addOnIdsPerGuest: [[]],
        selectedAddOns: {}
      }));
    }
    isTabSwitchingRef.current = false;
  }, [incompleteCarts, selectedCartTab, state.eventBookingCategory, cartItemId]);

  // Check if cart needs checkout cancellation
  const shouldCancelCheckout = (cart: CartResponse | null): boolean => {
    if (!cart) return false;
    return cart.status === CartStatus.PendingPayment || cart.status === CartStatus.FailedPayment;
  };

  // Check if cart has an item for the current EBC
  const hasCartItemForEBC = (): boolean => {
    const currentCart = getCurrentCart();
    // Items array is already filtered by EBC - if empty, no item exists for this EBC
    return !!(currentCart && currentCart.items && currentCart.items.length > 0);
  };

  // Check if current UI state differs from cart
  const hasCartChanges = (): boolean => {
    const currentCart = getCurrentCart();
    // Items array is already filtered by EBC - if empty, no item exists for this EBC
    if (!currentCart || !currentCart.items || currentCart.items.length === 0) {
      return false; // No cart item for this EBC, so no changes to detect
    }

    // The items array contains only items for this EBC
    const matchingItem = currentCart.items[0];

    if (!matchingItem) {
      return true; // Shouldn't happen, but if it does, treat as needing to add
    }

    // Compare guest count
    if (matchingItem.total_guests !== state.guestCount) {
      return true;
    }

    // Convert cart's add_ons_per_guest from SelectedAddOn[][] to string[][] for comparison
    // Expand quantities to match the UI state format
    const cartAddOns = matchingItem.add_ons_per_guest?.map(guestAddOns => {
      const expandedIds: string[] = [];
      guestAddOns?.forEach(addOn => {
        if (addOn) {
          const qty = addOn.quantity || 1;
          for (let i = 0; i < qty; i++) {
            expandedIds.push(addOn.add_on_id);
          }
        }
      });
      return expandedIds.sort();
    }) || [[]];

    const uiGuestAddOns = state.addOnIdsPerGuest.map(guestAddOns => [...guestAddOns].sort());

    // Compare add-ons per guest
    if (cartAddOns.length !== uiGuestAddOns.length) {
      return true;
    }

    // Compare each guest's add-ons
    for (let i = 0; i < cartAddOns.length; i++) {
      if (cartAddOns[i].length !== uiGuestAddOns[i].length) {
        return true;
      }

      for (let j = 0; j < cartAddOns[i].length; j++) {
        if (cartAddOns[i][j] !== uiGuestAddOns[i][j]) {
          return true;
        }
      }
    }

    return false;
  };

  // Switch cart tab — save current tab's local state before switching
  const switchCartTab = (tab: 'main' | 'guest') => {
    // Save current tab's local state so it can be restored later
    if (selectedCartTab && selectedCartTab !== tab) {
      savedStatePerTab.current[selectedCartTab] = {
        guestCount: state.guestCount,
        addOnIdsPerGuest: state.addOnIdsPerGuest,
        selectedAddOns: state.selectedAddOns,
      };
    }
    isTabSwitchingRef.current = true;
    setSelectedCartTab(tab);
  };

  // Update addOnIdsPerGuest when guest count changes
  useEffect(() => {
    setState(prev => {
      const currentGuests = prev.addOnIdsPerGuest.length;
      const newGuestCount = prev.guestCount;

      if (newGuestCount === 0) {
        // Clear all add-ons when guest count is 0
        return {
          ...prev,
          addOnIdsPerGuest: [],
          selectedAddOns: {}
        };
      } else if (newGuestCount > currentGuests) {
        // Find all unique addons that have been selected for any guest
        // New guests should receive all these addons (one of each)
        const allSelectedAddOnIds = new Set<string>();

        prev.addOnIdsPerGuest.forEach(guestAddOns => {
          guestAddOns.forEach(addOnId => {
            allSelectedAddOnIds.add(addOnId);
          });
        });

        // Create new guests with all selected addons (each gets one of each addon)
        const selectedAddOnsArray = Array.from(allSelectedAddOnIds);
        const newGuests = Array(newGuestCount - currentGuests)
          .fill(null)
          .map(() => [...selectedAddOnsArray]);

        return {
          ...prev,
          addOnIdsPerGuest: [...prev.addOnIdsPerGuest, ...newGuests]
        };
      } else if (newGuestCount < currentGuests) {
        // Remove excess guests
        return {
          ...prev,
          addOnIdsPerGuest: prev.addOnIdsPerGuest.slice(0, newGuestCount)
        };
      }

      return prev;
    });
  }, [state.guestCount]);

  // Calculate price breakdown
  const priceBreakdown = useMemo((): PriceBreakdown => {
    if (!state.eventBookingCategory) {
      return {
        basePrice: 0,
        addOnPrice: 0,
        totalPrice: 0,
        perGuestBreakdown: []
      };
    }

    const basePrice = state.eventBookingCategory.price;
    const totalBasePrice = basePrice * state.guestCount;

    let totalAddOnPrice = 0;
    const perGuestBreakdown = state.addOnIdsPerGuest.map((guestAddOns, guestIndex) => {
      const guestAddOnDetails = guestAddOns.map(addOnId => {
        const addOnSchedule = state.eventBookingCategory?.add_on_schedules.find(
          aos => aos.add_on.id === addOnId
        );
        if (!addOnSchedule) return null;

        return {
          addOn: addOnSchedule,
          quantity: 1, // Each add-on appears once per guest
          price: addOnSchedule.price
        };
      }).filter(Boolean) as Array<{
        addOn: AddOnScheduleData;
        quantity: number;
        price: number;
      }>;

      const guestAddOnPrice = guestAddOnDetails.reduce((sum, item) => sum + item.price, 0);
      totalAddOnPrice += guestAddOnPrice;

      return {
        guestIndex,
        addOns: guestAddOnDetails,
        totalAddOnPrice: guestAddOnPrice
      };
    });

    return {
      basePrice: totalBasePrice,
      addOnPrice: totalAddOnPrice,
      totalPrice: totalBasePrice + totalAddOnPrice,
      perGuestBreakdown
    };
  }, [state.eventBookingCategory, state.guestCount, state.addOnIdsPerGuest]);

  // Actions
  const setGuestCount = (count: number) => {
    // Allow count of 0 only when there's a cart item (pre-selected mode)
    const hasItem = hasCartItemForEBC();
    if (count < 0 || (count < 1 && !hasItem)) return;
    setState(prev => ({ ...prev, guestCount: count }));
  };

  const addGuest = () => {
    setState(prev => ({ ...prev, guestCount: prev.guestCount + 1 }));
  };

  const removeGuest = () => {
    if (state.guestCount > 1) {
      setState(prev => ({ ...prev, guestCount: prev.guestCount - 1 }));
    }
  };

  const addAddOnToAllGuests = (addOnId: string, quantity: number) => {
    setState(prev => {
      const newSelectedAddOns = { ...prev.selectedAddOns };
      newSelectedAddOns[addOnId] = quantity;

      // Add this add-on to ALL current guests
      const newAddOnIdsPerGuest = prev.addOnIdsPerGuest.map(guestAddOns => {
        // Remove existing instances of this add-on first
        const filteredAddOns = guestAddOns.filter(id => id !== addOnId);
        // Add the new quantity
        const newAddOns = Array(quantity).fill(addOnId);
        return [...filteredAddOns, ...newAddOns];
      });

      return {
        ...prev,
        selectedAddOns: newSelectedAddOns,
        addOnIdsPerGuest: newAddOnIdsPerGuest
      };
    });
  };

  const removeAddOnFromAllGuests = (addOnId: string) => {
    setState(prev => {
      const newSelectedAddOns = { ...prev.selectedAddOns };
      delete newSelectedAddOns[addOnId];

      // Remove this add-on from ALL guests
      const newAddOnIdsPerGuest = prev.addOnIdsPerGuest.map(guestAddOns =>
        guestAddOns.filter(id => id !== addOnId)
      );

      return {
        ...prev,
        selectedAddOns: newSelectedAddOns,
        addOnIdsPerGuest: newAddOnIdsPerGuest
      };
    });
  };

  const handleAddOnToggle = (addOnId: string, isSelected: boolean, guestIndex?: number) => {
    // Prevent adding add-ons when guest count is 0
    if (isSelected && state.guestCount === 0) {
      return;
    }

    if (guestIndex !== undefined) {
      // Toggle for specific guest
      toggleAddOnForSpecificGuest(addOnId, guestIndex);
    } else {
      // Toggle for all guests - but check inventory first
      if (isSelected) {
        // Find the add-on to check inventory
        const addOnSchedule = state.eventBookingCategory?.add_on_schedules.find(
          aos => aos.add_on.id === addOnId
        );

        if (addOnSchedule && (addOnSchedule.remaining_inventory === null || addOnSchedule.remaining_inventory >= state.guestCount)) {
          // Sufficient or unlimited inventory - apply to all guests
          addAddOnToAllGuests(addOnId, 1);
        } else if (addOnSchedule && addOnSchedule.remaining_inventory !== null && addOnSchedule.remaining_inventory > 0) {
          // Limited inventory - only add to selectedAddOns count, don't auto-assign to guests
          setState(prev => ({
            ...prev,
            selectedAddOns: {
              ...prev.selectedAddOns,
              [addOnId]: Math.min(addOnSchedule.remaining_inventory as number, state.guestCount)
            }
          }));
        }
      } else {
        removeAddOnFromAllGuests(addOnId);
      }
    }
  };

  const toggleAddOnForSpecificGuest = (addOnId: string, guestIndex: number) => {
    // Prevent adding add-ons when guest count is 0
    if (state.guestCount === 0) {
      return;
    }

    setState(prev => {
      // Find the add-on to check inventory
      const addOnSchedule = prev.eventBookingCategory?.add_on_schedules.find(
        aos => aos.add_on.id === addOnId
      );

      if (!addOnSchedule) return prev;

      const newAddOnIdsPerGuest = [...prev.addOnIdsPerGuest];
      const guestAddOns = [...newAddOnIdsPerGuest[guestIndex]];

      const addOnIndex = guestAddOns.indexOf(addOnId);
      if (addOnIndex > -1) {
        // Remove the add-on from this guest
        guestAddOns.splice(addOnIndex, 1);
      } else {
        // Check if we can add the add-on to this guest
        const currentTotalCount = newAddOnIdsPerGuest.reduce((count, guestAddOns) =>
          count + guestAddOns.filter(id => id === addOnId).length, 0
        );

        if (addOnSchedule.remaining_inventory === null || currentTotalCount < addOnSchedule.remaining_inventory) {
          // Add the add-on to this guest
          guestAddOns.push(addOnId);
        } else {
          // Can't add more - inventory limit reached
          return prev;
        }
      }

      newAddOnIdsPerGuest[guestIndex] = guestAddOns;

      // Update selectedAddOns count
      const newSelectedAddOns = { ...prev.selectedAddOns };
      const totalCount = newAddOnIdsPerGuest.reduce((count, guestAddOns) =>
        count + guestAddOns.filter(id => id === addOnId).length, 0
      );

      if (totalCount > 0) {
        newSelectedAddOns[addOnId] = totalCount;
      } else {
        delete newSelectedAddOns[addOnId];
      }

      return {
        ...prev,
        selectedAddOns: newSelectedAddOns,
        addOnIdsPerGuest: newAddOnIdsPerGuest
      };
    });
  };

  // Set specific quantity of an add-on for a specific guest
  const setAddOnQuantityForGuest = (addOnId: string, guestIndex: number, quantity: number) => {
    // Prevent adding add-ons when guest count is 0
    if (state.guestCount === 0) {
      return;
    }

    setState(prev => {
      // Find the add-on to check inventory
      const addOnSchedule = prev.eventBookingCategory?.add_on_schedules.find(
        aos => aos.add_on.id === addOnId
      );

      if (!addOnSchedule) return prev;

      const newAddOnIdsPerGuest = [...prev.addOnIdsPerGuest.map(g => [...g])];

      // Calculate current usage by OTHER guests
      let usedByOthers = 0;
      newAddOnIdsPerGuest.forEach((guestAddOns, idx) => {
        if (idx !== guestIndex) {
          usedByOthers += guestAddOns.filter(id => id === addOnId).length;
        }
      });

      // Calculate max quantity for this guest
      const maxQuantity = addOnSchedule.remaining_inventory === null
        ? Infinity
        : addOnSchedule.remaining_inventory - usedByOthers;
      const finalQuantity = Math.min(Math.max(0, quantity), maxQuantity);

      // Remove all instances of this add-on from this guest
      newAddOnIdsPerGuest[guestIndex] = newAddOnIdsPerGuest[guestIndex].filter(id => id !== addOnId);

      // Add the correct number of instances
      for (let i = 0; i < finalQuantity; i++) {
        newAddOnIdsPerGuest[guestIndex].push(addOnId);
      }

      // Update selectedAddOns count
      const newSelectedAddOns = { ...prev.selectedAddOns };
      const totalCount = newAddOnIdsPerGuest.reduce((count, guestAddOns) =>
        count + guestAddOns.filter(id => id === addOnId).length, 0
      );

      if (totalCount > 0) {
        newSelectedAddOns[addOnId] = totalCount;
      } else {
        delete newSelectedAddOns[addOnId];
      }

      return {
        ...prev,
        selectedAddOns: newSelectedAddOns,
        addOnIdsPerGuest: newAddOnIdsPerGuest
      };
    });
  };

  const getAddOnCountForAddOn = (addOnId: string): number => {
    return state.addOnIdsPerGuest.reduce((count, guestAddOns) =>
      count + guestAddOns.filter(id => id === addOnId).length, 0
    );
  };

  const getGuestAddOns = (guestIndex: number): string[] => {
    return state.addOnIdsPerGuest[guestIndex] || [];
  };

  const deleteCartItem = async (): Promise<void> => {
    const currentCart = getCurrentCart();
    // Items array is already filtered by EBC - if empty, no item exists for this EBC
    if (!currentCart || !currentCart.items || currentCart.items.length === 0) {
      throw new Error('No cart item to delete');
    }

    // The items array contains only items for this EBC
    const matchingItem = currentCart.items[0];

    if (!matchingItem) {
      throw new Error('Cart item not found for this booking category');
    }

    setIsUpdatingCart(true);

    try {
      // Check if checkout needs to be cancelled
      if (shouldCancelCheckout(currentCart)) {
        try {
          await cartService.cancelCheckout(currentCart.id);
        } catch (error) {
          console.error('Error canceling checkout:', error);
          throw new Error('Couldn\'t cancel previous checkout. Please try again or contact support.');
        }
      }

      await cartService.removeCartItem(matchingItem.id);

      // Refetch both EBC data and incomplete carts to sync state
      try {
        await fetchData(true); // pass true to skip the loading state which causes the flicker
      } catch (error) {
        // Log but don't fail the delete operation if refetch fails
        console.warn('Failed to refetch data after delete:', error);
      }
      // Cart count will auto-refresh via header's stale-while-revalidate
    } catch (error) {
      console.error('Error in deleteCartItem:', error);
      // Re-throw with user-friendly message if needed
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to remove item from cart. Please try again.');
    } finally {
      setIsUpdatingCart(false);
    }
  };

  const updateCart = async (): Promise<{ cartId: string }> => {
    if (!state.eventBookingCategory) {
      throw new Error('No booking category selected');
    }

    const currentCart = getCurrentCart();
    // Items array is already filtered by EBC - if empty, no item exists for this EBC
    if (!currentCart || !currentCart.items || currentCart.items.length === 0) {
      throw new Error('No cart item to update');
    }

    // The items array contains only items for this EBC
    const matchingItem = currentCart.items[0];

    if (!matchingItem) {
      throw new Error('Cart item not found for this booking category');
    }

    setIsUpdatingCart(true);

    try {
      // Check if checkout needs to be cancelled
      if (shouldCancelCheckout(currentCart)) {
        try {
          await cartService.cancelCheckout(currentCart.id);
        } catch (error) {
          console.error('Error canceling checkout:', error);
          throw new Error('Couldn\'t cancel previous checkout. Please try again or contact support.');
        }
      }

      // Transform addOnIdsPerGuest (string[][]) to AddOnSelection[][] format
      // Each addon ID in the array represents quantity 1 of that addon
      // Multiple occurrences of the same addon ID are aggregated into quantity
      const transformToSelections = (addOnIdsPerGuest: string[][]): AddOnSelection[][] => {
        return addOnIdsPerGuest.map(guestAddOns => {
          const counts = new Map<string, number>();
          guestAddOns.forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
          return Array.from(counts.entries()).map(([add_on_id, quantity]) => ({ add_on_id, quantity }));
        });
      };

      const updateBody = {
        total_guests: state.guestCount,
        add_on_selections_per_guest: transformToSelections(state.addOnIdsPerGuest)
      };

      await cartService.updateCartItem(matchingItem.id, updateBody);

      // Refetch both EBC data and incomplete carts to sync state
      try {
        await fetchData(true); // pass true to skip the loading state which causes the flicker
      } catch (error) {
        // Log but don't fail the update operation if refetch fails
        console.warn('Failed to refetch data after update:', error);
      }
      // Cart count will auto-refresh via header's stale-while-revalidate

      return { cartId: currentCart.id };
    } catch (error) {
      console.error('Error in updateCart:', error);
      // Re-throw with user-friendly message if needed
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to update cart. Please try again.');
    } finally {
      setIsUpdatingCart(false);
    }
  };

  const addToCart = async (): Promise<{ cartId: string }> => {
    if (!state.eventBookingCategory) {
      throw new Error('No booking category selected');
    }

    setState(prev => ({ ...prev, addingToCart: true }));
    isAddingToCartRef.current = true;

    try {
      // For unauthenticated users, register as guest first
      if (!isAuthenticated) {
        try {
          await authService.registerGuest();
          // After guest registration, refetch auth to update context
          // This ensures the auth state is updated and cookie is recognized by subsequent requests
          await refetchAuth();
        } catch (error) {
          console.error('Error registering guest:', error);
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
            throw new Error('Network error. Please check your connection and try again.');
          }
          throw new Error('Failed to register as guest. Please try again or sign in.');
        }
      }

      // Check if we have a cart that needs checkout cancellation
      const currentCart = getCurrentCart();
      if (currentCart && shouldCancelCheckout(currentCart)) {
        try {
          await cartService.cancelCheckout(currentCart.id);
        } catch (error) {
          console.error('Error canceling checkout:', error);
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          if (errorMessage.includes('timeout')) {
            throw new Error('Request timed out. Please try again.');
          }
          throw new Error('Couldn\'t cancel previous checkout. Please try again or contact support.');
        }
      }

      // Transform addOnIdsPerGuest (string[][]) to AddOnSelection[][] format
      const transformToSelections = (addOnIdsPerGuest: string[][]): AddOnSelection[][] => {
        return addOnIdsPerGuest.map(guestAddOns => {
          const counts = new Map<string, number>();
          guestAddOns.forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
          return Array.from(counts.entries()).map(([add_on_id, quantity]) => ({ add_on_id, quantity }));
        });
      };

      const cartItem = {
        total_guests: state.guestCount,
        add_on_selections_per_guest: transformToSelections(state.addOnIdsPerGuest)
      };

      const result = await cartService.addNonTableBookingCartItem(state.eventBookingCategory.id, cartItem);

      // Manually fetch incomplete carts to ensure we have the latest data
      // This is needed because fetchData() might use stale isAuthenticated from closure
      try {
        if (eventBookingCategoryId && !isLoadingCartsRef.current) {
          isLoadingCartsRef.current = true;
          setIsLoadingCarts(true);
          const carts = await cartService.getIncompleteCartsForEBC(eventBookingCategoryId);

          // Handle edge case: if more than 2 carts, log error and take most recent 2
          if (carts.length > 2) {
            console.error(`Unexpected: Found ${carts.length} incomplete carts for EBC. Taking most recent 2.`);
            const sortedCarts = [...carts].sort((a, b) => {
              return new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime();
            });
            carts.splice(0, carts.length, ...sortedCarts.slice(0, 2));
          }

          setIncompleteCarts(carts);

          // Set default tab using helper function that considers initialCartTab preference
          const defaultTab = determineDefaultTab(carts);
          setSelectedCartTab(defaultTab);

          setIsLoadingCarts(false);
          isLoadingCartsRef.current = false;
        }

        // Reset flag after carts are fetched to allow future useEffect triggers
        isAddingToCartRef.current = false;
      } catch (error) {
        // Log but don't fail the add operation if cart fetch fails
        console.warn('Failed to fetch carts after add to cart:', error);
        setIsLoadingCarts(false);
        isLoadingCartsRef.current = false;
        // Still reset the flag even if fetch fails
        isAddingToCartRef.current = false;
      }
      // Cart count will auto-refresh via header's stale-while-revalidate

      // Clear saved per-tab state for the synced tab — local changes are now in the cart
      if (selectedCartTab) {
        savedStatePerTab.current[selectedCartTab] = null;
      }

      return result;
    } catch (error) {
      console.error('Error in addToCart:', error);
      setState(prev => ({ ...prev, addingToCart: false }));
      // Reset flag on error as well
      isAddingToCartRef.current = false;
      // Re-throw with user-friendly message if needed
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to add item to cart. Please try again.');
    } finally {
      setState(prev => ({ ...prev, addingToCart: false }));
    }
  };

  const currentCart = getCurrentCart();
  const hasItemForEBC = hasCartItemForEBC();
  const hasChanges = isTabSwitchingRef.current ? false : hasCartChanges();

  // Compute the real-time item count for a given tab
  // For the active tab: 1 if guestCount > 0, else 0
  // For the inactive tab: check saved state, then fall back to server-side cart count
  const getItemCountForTab = (tab: 'main' | 'guest'): number => {
    if (tab === selectedCartTab) {
      // Active tab — count from current local state
      return state.guestCount > 0 ? 1 : 0;
    }

    // Inactive tab — check saved local state first
    const saved = savedStatePerTab.current[tab];
    if (saved) {
      return saved.guestCount > 0 ? 1 : 0;
    }

    // Fall back to server-side cart item count
    const cart = incompleteCarts?.find(c =>
      tab === 'main' ? !c.is_guest_cart : c.is_guest_cart
    );
    return cart?.items?.length ?? 0;
  };

  return {
    ...state,
    priceBreakdown,
    setGuestCount,
    addGuest,
    removeGuest,
    addAddOnToAllGuests,
    removeAddOnFromAllGuests,
    handleAddOnToggle,
    toggleAddOnForSpecificGuest,
    setAddOnQuantityForGuest,
    getAddOnCountForAddOn,
    getGuestAddOns,
    addToCart,
    updateCart,
    deleteCartItem,
    // Cart-related exports
    incompleteCarts,
    selectedCartTab,
    currentCart,
    isLoadingCarts,
    isUpdatingCart,
    hasCartItemForEBC: hasItemForEBC,
    hasCartChanges: hasChanges,
    switchCartTab,
    getItemCountForTab,
    // Refetch function to manually trigger data refresh
    refetch: fetchData,
  };
};
